import { useEffect, useRef, useState } from "react";

/**
 * The Hopsy hay journey — eight hand-drawn sketch vignettes with animated
 * step reveal on scroll-into-view. Replaces the single farm image in the
 * Brand Story section.
 */
const STEPS = [
  { slug: "harvest",   label: "Harvested",     caption: "Sun-drenched fields cut at peak ripeness." },
  { slug: "select",    label: "Hand-Selected", caption: "Only the long, green strands make the cut." },
  { slug: "transport", label: "To the Mill",   caption: "Gently trucked from meadow to sorting floor." },
  { slug: "bale",      label: "Baled",         caption: "Pressed into round and square bales with care." },
  { slug: "sort",      label: "Hand-Sorted",   caption: "Dust screened, debris removed, strand by strand." },
  { slug: "filter",    label: "Filtered",      caption: "Baled hay sifted to remove dust and fine debris." },
  { slug: "bagpack",   label: "Bagged",        caption: "Clean, long-strand hay packed into Hopsy bags." },
  { slug: "pack",      label: "Packed & Shipped", caption: "Sealed fresh in Hopsy pouches — on to happy homes." },
];

export default function HayJourney() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } });
      },
      { threshold: 0.18 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} data-testid="hay-journey" className="w-full">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-10">
        {STEPS.map((s, i) => (
          <div
            key={s.slug}
            data-testid={`journey-step-${s.slug}`}
            className="flex flex-col items-center text-center"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(14px)",
              transition: `opacity 700ms ease-out ${140 + i * 170}ms, transform 700ms ease-out ${140 + i * 170}ms`,
            }}
          >
            {/* Sketch */}
            <div className="relative w-full aspect-square">
              <img
                src={`/hopsy-step-${s.slug}.png`}
                alt={s.label}
                className="absolute inset-0 w-full h-full object-contain mix-blend-multiply"
                loading="lazy"
              />
              {/* Step numeral, subtle */}
              <span className="absolute top-1 left-1 md:top-2 md:left-2 font-serif italic text-xl md:text-2xl text-forest/30 select-none">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            {/* Label + caption */}
            <p className="mt-2 md:mt-3 font-serif text-lg md:text-xl text-forest leading-tight">
              {s.label}
            </p>
            <p className="mt-1 text-[10px] md:text-[11px] tracking-wide text-ink-muted font-light max-w-[22ch] leading-relaxed">
              {s.caption}
            </p>
          </div>
        ))}
      </div>

      {/* Dashed path underline evocative of a process flow */}
      <div className="hidden md:flex items-center gap-3 mt-10">
        <span className="text-[10px] uppercase tracking-[0.3em] text-sage">Meadow</span>
        <span className="flex-1 border-t border-dashed border-sage/60"></span>
        <span className="text-[10px] uppercase tracking-[0.3em] text-sage">Mealtime</span>
      </div>
    </div>
  );
}
